// /admin/orgs/:id/reports — per-org report roster + support-access mint
// entry point (ADR-0094 §10 / Postscript 3, ticket #0487, core-api
// route #0494).
//
// GET /admin/orgs/:id/reports is live — this screen renders the real
// roster, not a placeholder. Row shape is adminOrgReportRowSchema
// (@bitacorapro/api-types) — a security/compliance-ruled subset of
// reportSchema; `signatureStatus` is a derived, non-reversible
// tri-state, never the raw constanciaNoFirmaRole/Motivo.
//
// Mint flow: ADR-0094 §2/§6 requires `reason` (non-empty) and a fresh
// 6-digit TOTP per mint, plus `organizationId` (auth-svc cannot resolve
// org from reportId — services never cross-read each other's DBs). Each
// row expands an inline reason+TOTP form before calling
// useMintSupportGrant().mutateAsync() — never a bare one-click button.

import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { FormEvent } from "react";
import { EmptyState } from "../../components/EmptyState";
import { PageError } from "../../components/PageError";
import { PageSkeleton } from "../../components/PageSkeleton";
import { Skel } from "../../components/Skel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import { ApiError } from "../../lib/api/error";
import { env } from "../../lib/env";
import { useQueryView } from "../../lib/loading/useQueryView";
import type { AdminOrgReportRow } from "@bitacorapro/api-types";
import { useAdminOrg, useAdminOrgReports, useMintSupportGrant } from "./queries";

// Extracted so ADR-0094 §10's rule ("SOLO el code en la URL, nunca el
// reportId como query param separado") is testable as a pure function,
// independent of the mutation/component wiring around it.
export function buildSupportAccessUrl(pwaBaseUrl: string, code: string): string {
  return `${pwaBaseUrl.replace(/\/+$/, "")}/soporte/${encodeURIComponent(code)}`;
}

const ROSTER_COLUMNS = ["Folio", "Tipo", "Estado", "Creado", "Acceso de soporte"];

export function AdminOrgReports() {
  const { id = "" } = useParams<{ id: string }>();
  const orgQuery = useAdminOrg(id);
  const reportsQuery = useAdminOrgReports(id);
  // Two independent queries, so two views by hand (not QueryBoundary):
  // the org feeds only the header's counts line; the roster owns the
  // body, and its error keeps the API's own message (support needs it).
  const orgView = useQueryView(orgQuery);
  const rosterView = useQueryView(reportsQuery, { isEmpty: (d) => d.reports.length === 0 });

  return (
    <div className="page">
      <div className="page-pad">
        <header className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <a href={`/admin/orgs/${id}`}>← {orgQuery.data?.name ?? "Organización"}</a>
            </div>
            <h1 className="page-header__title">Reportes</h1>
            <p className="page-header__sub">
              {orgView.kind === "ready" ? (
                `${orgView.data.counts.reports} total · ${orgView.data.counts.signedReports} firmado${orgView.data.counts.signedReports === 1 ? "" : "s"} o cerrado${orgView.data.counts.signedReports === 1 ? "" : "s"}`
              ) : orgView.kind === "loading" ? (
                <Skel variant="text" width={220} />
              ) : null}
            </p>
          </div>
        </header>

        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Roster</h2>
          </div>
          <div className="panel__body">
            {rosterView.kind === "loading" ? (
              <>
                <PageSkeleton label="Cargando los reportes…" visible={rosterView.visible}>
                  <SkelTable
                    rows={8}
                    cols={ROSTER_COLUMNS.length}
                    headers={ROSTER_COLUMNS}
                    compact
                  />
                </PageSkeleton>
                {rosterView.slow && (
                  <div className="notice-banner notice-banner--action">
                    <span>
                      <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión.
                      Seguimos intentando.
                    </span>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => void reportsQuery.refetch()}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </>
            ) : rosterView.kind === "error" ? (
              <PageError
                title={rosterView.offline ? "Sin conexión" : "No pudimos cargar los reportes"}
                description={
                  rosterView.offline
                    ? "Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                    : rosterView.error instanceof ApiError
                      ? rosterView.error.message
                      : "Ocurrió un error inesperado."
                }
                onRetry={rosterView.retry}
              />
            ) : rosterView.kind === "empty" || rosterView.kind === "disabled" ? (
              <EmptyState title="Esta organización todavía no tiene reportes" />
            ) : (
              <>
                {rosterView.refreshFailed && (
                  <div className="notice-banner notice-banner--action">
                    <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => void reportsQuery.refetch()}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                <table
                  className="dtable dtable--compact"
                  aria-label={`Reportes de ${orgQuery.data?.name ?? "la organización"}`}
                >
                  <thead>
                    <tr>
                      {ROSTER_COLUMNS.map((c) => (
                        <th key={c} scope="col">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rosterView.data.reports.map((r) => (
                      <ReportRow key={r.id} report={r} organizationId={id} />
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function ReportRow({
  report,
  organizationId,
}: {
  report: AdminOrgReportRow;
  organizationId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ url: string; expiresAt: string } | null>(null);
  const mint = useMintSupportGrant();
  // Synchronous reentrancy guard — mint.isPending lags one render behind
  // a fast double-click / held-Enter, which can fire handleMint twice
  // before React commits isPending: true. Checked-and-set before any
  // await, so the second call is rejected in the same tick regardless
  // of render timing.
  const inFlightRef = useRef(false);

  const canSubmit =
    reason.trim().length > 0 && /^\d{6}$/.test(totpCode) && !mint.isPending && !inFlightRef.current;
  const pwaBaseUrl = env.pwaBaseUrl;
  const panelId = `support-access-panel-${report.id}`;

  function handleToggleExpanded() {
    setExpanded((v) => !v);
    // Collapsing then re-expanding must not resurface a stale, possibly
    // already-expired mint result or a stale error from a prior attempt.
    setMinted(null);
    setError(null);
    mint.reset();
  }

  async function handleMint(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    // The button that calls this only renders when pwaBaseUrl is set
    // (see the JSX below), but that narrowing doesn't survive the
    // closure — guard explicitly rather than asserting.
    if (!pwaBaseUrl) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      const res = await mint.mutateAsync({
        organizationId,
        scopeResourceId: report.id,
        reason: reason.trim(),
        totpCode,
      });
      // Only `code` crosses into the URL — never reportId or any other
      // identifier as a separate query param (ADR-0094 §10).
      setMinted({
        url: buildSupportAccessUrl(pwaBaseUrl, res.code),
        expiresAt: res.expiresAt,
      });
      setReason("");
      setTotpCode("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err)
      );
    } finally {
      inFlightRef.current = false;
    }
  }

  return (
    <>
      <tr>
        <td>
          <span className="cell-strong">{report.folio ?? "—"}</span>
        </td>
        <td>{report.reportType}</td>
        <td>
          <span className="st-pill st-pill--idle">{report.status}</span>
        </td>
        <td>
          <span className="cell-meta">
            {new Date(report.createdAt).toLocaleDateString("es-CL")}
          </span>
        </td>
        <td>
          {pwaBaseUrl ? (
            <button
              type="button"
              className="btn btn--secondary"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={handleToggleExpanded}
            >
              {expanded ? "Cancelar" : "Generar acceso de soporte"}
            </button>
          ) : (
            <span className="cell-meta">PWA no configurada (VITE_PWA_BASE_URL)</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr id={panelId}>
          <td colSpan={5}>
            <form onSubmit={(e) => void handleMint(e)} className="support-access-panel">
              <label className="cell-meta" htmlFor={`reason-${report.id}`}>
                Motivo del acceso de soporte
              </label>
              <input
                id={`reason-${report.id}`}
                type="text"
                className="input"
                placeholder="Ej: verificar que el acta quedó completa tras el incidente reportado por el cliente"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={mint.isPending}
              />
              <label className="cell-meta" htmlFor={`totp-${report.id}`}>
                Código de verificación (6 dígitos)
              </label>
              <input
                id={`totp-${report.id}`}
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                className="input"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                disabled={mint.isPending}
              />
              <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {mint.isPending ? "Generando…" : "Generar acceso"}
              </button>
              {error && (
                <span className="auth-error" role="alert">
                  {error}
                </span>
              )}
              {minted && (
                <div className="cell-meta" role="status">
                  Acceso de soporte generado. Válido hasta las{" "}
                  {new Date(minted.expiresAt).toLocaleTimeString("es-CL", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                  ; después de eso el enlace deja de servir y hay que generar uno nuevo. Se
                  recomienda abrirlo en una pestaña nueva.{" "}
                  {/* ph-no-capture: the href AND the visible text are a bearer
                      support-access token. PostHog autocapture would ship both
                      (attr__href, $el_text) on click and replay would render it;
                      this class excludes the element from both. */}
                  <a
                    href={minted.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Abrir acceso de soporte en una pestaña nueva"
                    className="ph-no-capture"
                  >
                    <code>{minted.url}</code>
                  </a>
                </div>
              )}
            </form>
          </td>
        </tr>
      )}
    </>
  );
}
